"use client"
import { useState, useEffect, useRef } from "react";
import { Button, Textarea } from "@heroui/react";
import EmojiKeyboard from "./emoji";
import { Smile, X } from "lucide-react";
import { useUserInfoStore } from "@/models/userInfo";
import clsx from "clsx";

interface ReplyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (content: string, images?: { url: string; height: number; width: number }[]) => void;
    replyToUser: {
        nickname: string;
        profile: string;
    };
    isMobile?: boolean;
}

const ReplyModal = ({ isOpen, onClose, onSubmit, replyToUser, isMobile = false }: ReplyModalProps) => {
    const [replyContent, setReplyContent] = useState("");
    const [images, setImages] = useState<{ url: string; height: number; width: number }[]>([]);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const { userInfo } = useUserInfoStore();
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleSubmit = () => {
        onSubmit(replyContent, images);
        setReplyContent("");
        setImages([]);
        onClose();
    };

    // focus后光标在最后
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
                }
            }, 100);
        }
    }, [isOpen]);

    // 键盘监听处理
    // useEffect(() => {
    //     if (!isMobile || !isOpen) return;

    //     let initialViewportHeight = window.innerHeight;

    //     const updateKeyboardState = () => {
    //         const currentHeight = window.visualViewport?.height || window.innerHeight;
    //         const heightDifference = initialViewportHeight - currentHeight;

    //         if (heightDifference > 150) {
    //             setIsKeyboardVisible(true);
    //             setKeyboardHeight(heightDifference);
    //         } else {
    //             setIsKeyboardVisible(false);
    //             setKeyboardHeight(0);
    //         }
    //     };

    //     const handleFocusIn = (e: FocusEvent) => {
    //         const target = e.target;
    //         if (target && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
    //             if (containerRef.current && containerRef.current.contains(target)) {
    //                 setTimeout(() => {
    //                     updateKeyboardState();

    //                     if (isOpen && containerRef.current) {
    //                         containerRef.current.scrollIntoView({
    //                             behavior: 'smooth',
    //                             block: 'end',
    //                             inline: 'nearest'
    //                         });
    //                     }
    //                 }, 300);
    //             }
    //         }
    //     };

    //     const handleFocusOut = (e: FocusEvent) => {
    //         const target = e.target;
    //         if (target && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
    //             if (containerRef.current && containerRef.current.contains(target)) {
    //                 setTimeout(() => {
    //                     if (!isOpen) {
    //                         setIsKeyboardVisible(false);
    //                         setKeyboardHeight(0);
    //                     }
    //                 }, 150);
    //             }
    //         }
    //     };

    //     const handleViewportChange = () => {
    //         updateKeyboardState();
    //     };

    //     if (window.visualViewport) {
    //         window.visualViewport.addEventListener('resize', handleViewportChange);
    //     }

    //     document.addEventListener('focusin', handleFocusIn);
    //     document.addEventListener('focusout', handleFocusOut);

    //     return () => {
    //         if (window.visualViewport) {
    //             window.visualViewport.removeEventListener('resize', handleViewportChange);
    //         }
    //         document.removeEventListener('focusin', handleFocusIn);
    //         document.removeEventListener('focusout', handleFocusOut);
    //     };
    // }, [isOpen, isMobile]);

    // 计算输入框的偏移量
    // const getTransform = () => {
    //     if (!isMobile || !isKeyboardVisible) return 'translateY(0)';

    //     const offset = Math.max(keyboardHeight - 20, 0);
    //     return `translateY(-${offset}px)`;
    // };

    // 点击回复评论后100ms自动focus 
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 300);
        }
    }, [isOpen]);
    // 重置状态当关闭时
    useEffect(() => {
        if (!isOpen) {
            setReplyContent("");
            setImages([]);
            setIsKeyboardVisible(false);
            setKeyboardHeight(0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            {/* 遮罩层 */}
            <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={onClose}
            />

            {/* 底部弹出框 */}
            <div
                ref={containerRef}
                className="fixed left-0 right-0 bottom-0 bg-white z-50 transition-transform duration-300 safe-bottom"
            // style={{
            //     transform: getTransform(),
            // }}
            >
                {/* 头部区域 - 显示回复目标 */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <img
                            src={replyToUser.profile}
                            alt={replyToUser.nickname}
                            className="w-8 h-8 rounded-full"
                        />
                        <div>
                            <span className="text-sm text-gray-500">Reply to</span>
                            <span className="text-sm font-medium text-gray-900 ml-1">{replyToUser.nickname}</span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* 输入区域 */}
                <div className="p-4">
                    <div className="flex flex-col space-y-3 max-w-[900px] mx-auto">
                        <Textarea
                            ref={textareaRef}
                            placeholder="Write your reply..."
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            className="bg-transparent border-0 text-gray-900 placeholder-gray-500 focus:ring-0 min-h-[80px] selectable"
                            style={{
                                fontSize: '16px', // 防止移动端浏览器自动放大
                                lineHeight: '1.5'
                            }}
                            onFocus={() => {
                                // 确保状态正确
                                if (!isOpen) return;
                            }}
                        />

                        {/* Emoji快捷选择 */}
                        {isMobile && (
                            <div className="w-full flex justify-around text-xl">
                                {MOBILE_EMOJI_LIST.map((emoji: string) => (
                                    <span
                                        key={emoji}
                                        className="cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setReplyContent(prev => prev + emoji);
                                        }}
                                    >
                                        {emoji}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* 底部操作区 */}
                        <div className="flex justify-between items-center">
                            <div className="flex space-x-4 items-center">
                                <EmojiKeyboard
                                    onEmojiSelect={(emoji) => {
                                        setReplyContent(prev => prev + emoji);
                                    }}
                                >
                                    <button className="text-gray-600 hover:text-gray-900">
                                        <Smile size={20} />
                                    </button>
                                </EmojiKeyboard>
                            </div>
                            <div className="flex space-x-2">
                                <Button
                                    onPress={onClose}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onPress={handleSubmit}
                                    disabled={!replyContent.trim() && images.length === 0}
                                    className="px-4 py-2 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Reply
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const MOBILE_EMOJI_LIST = [
    "😆", // 笑
    "🥰", // 亲亲
    "😮", // 惊讶
    "🤔", // 思考
    "👍🏻", // 赞
    "🙏🏻", // 合十
    "🌸", // 花
    "💖", // 爱心
];

export default ReplyModal; 